import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { calculateNextServiceDate, getServiceInterval } from "../utils/serviceHelpers";
import Loader from "../components/Loader";
import ExpenseTracker from "./ExpenseTracker";

function Cashbook() {
  const COLLECTION_NAME = "CASHBOOK";
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("records");
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [expenseCount, setExpenseCount] = useState(0);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [category, setCategory] = useState("ALL");
  const [categories, setCategories] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [technicians, setTechnicians] = useState([]);

  const [form, setForm] = useState({
    ticket: "",
    worktype: "",
    customerdetail: "",
    mobile: "",
    date: "",
    income: "",
    incomeremarks: "",
    expense: "",
    expenseremark: "",
    profit: "",
    techno: "",
    createddate: "",
    completeddate: "",
    paymentStatus: "Pending", // Default to Pending
    pendingAmount: "",
    paymentRemarks: "",
  });


  /* ---------------- DATE HELPERS ---------------- */
  const toDateObj = (v) => {
    if (v instanceof Timestamp) return new Date(v.seconds * 1000);
    const d = new Date(v);
    return d.toString() === "Invalid Date" ? null : d;
  };

  const formatDate = (v) => {
    const d = toDateObj(v);
    if (!d) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  /* ---------------- LOAD DATA ---------------- */
  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(collection(db, COLLECTION_NAME));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setData(rows);
      setFiltered(rows);

      const cats = [...new Set(rows.map((r) => r.worktype))].filter(Boolean);
      setCategories(cats);

      // Also get expense tracker count
      const expSnap = await getDocs(collection(db, "EXPENSES"));
      setExpenseCount(expSnap.size);

      setLoading(false);
    };
    load();
  }, []);

  /* ---------------- LOAD TECHNICIANS (EMPLOYEES) ---------------- */
  useEffect(() => {
    const loadTechs = async () => {
      try {
        const snap = await getDocs(collection(db, "EMPLOYEES"));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const techs = rows.map((r) => ({
          mobile: r.mobile || r.mobileNo || r.phone || r.mobileNumber || "",
          name: r.name || r.fullname || r.empName || r.employeeName || "",
        }));
        setTechnicians(techs.filter((t) => t.mobile));
      } catch (err) {
        console.error("Failed to load EMPLOYEES:", err);
      }
    };
    loadTechs();
  }, []);

  /* ---------------- FILTER ---------------- */
  useEffect(() => {
    let r = [...data];

    if (search)
      r = r.filter((row) =>
        JSON.stringify(row).toLowerCase().includes(search.toLowerCase())
      );

    if (fromDate)
      r = r.filter((row) => toDateObj(row.date) >= new Date(fromDate));

    if (toDate) {
      const td = new Date(toDate);
      td.setHours(23, 59, 59);
      r = r.filter((row) => toDateObj(row.date) <= td);
    }

    if (category !== "ALL")
      r = r.filter((row) => row.worktype === category);

    if (statusFilter !== "ALL")
      r = r.filter((row) => (row.paymentStatus || "Pending") === statusFilter);

    setFiltered(r);
    setPage(1);
  }, [search, fromDate, toDate, category, statusFilter, data]);

  /* ---------------- TOTALS ---------------- */
  const totalExpense = filtered.reduce(
    (s, r) => s + (Number(r.expense) || 0),
    0
  );
  const totalIncome = filtered.reduce(
    (s, r) => s + (Number(r.income) || 0),
    0
  );
  const totalProfit = totalIncome - totalExpense;

  /* ---------------- EXPORT EXCEL ---------------- */
  const exportToExcel = () => {
    if (!filtered.length) return alert("No records to export");

    const excelData = filtered.map((r, i) => ({
      "#": i + 1,
      CUSTOMERDETAIL: r.customerdetail,
      PROFIT: r.profit,
      INCOMEREMARKS: r.incomeremarks,
      TECHNO: r.techno,
      DATE: formatDate(r.date),
      INCOME: r.income,
      TICKET: r.ticket,
      WORKTYPE: r.worktype,
      MOBILE: r.mobile,
      EXPENSE: r.expense,
      EXPENSEREMARK: r.expenseremark,
      INCENTIVE: r.incentive,
      INCENTIVEREMARK: r.incentiveremark,
      INCENTIVEREMARK: r.incentiveremark,
      REMARKS: r.remarks,
      PAYMENT_STATUS: r.paymentStatus,
      PENDING_AMOUNT: r.pendingAmount,
      PAYMENT_REMARKS: r.paymentRemarks,
    }));

    excelData.push({});
    excelData.push({ CUSTOMERDETAIL: "TOTAL INCOME", INCOME: totalIncome });
    excelData.push({ CUSTOMERDETAIL: "TOTAL EXPENSE", EXPENSE: totalExpense });
    excelData.push({ CUSTOMERDETAIL: "TOTAL PROFIT", PROFIT: totalProfit });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cashbook");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Cashbook_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  /*---------------- ADD / EDIT ENTRY ---------------- */
  // (Implementation of add/edit form and logic would go here)
  const saveEntry = async () => {
    if (!form.customerdetail || !form.date) {
      alert("Customer & Date required");
      return;
    }

    const nextDate = calculateNextServiceDate(new Date(form.date), form.worktype);
    const interval = getServiceInterval(form.worktype);

    const payload = {
      ...form,
      income: Number(form.income || 0),
      expense: Number(form.expense || 0),
      date: Timestamp.fromDate(new Date(form.date)),
      profit: Number(form.income || 0) - Number(form.expense || 0),
      pendingAmount: form.paymentStatus === "Paid" ? 0 : Number(form.pendingAmount || 0),
      nextServiceDate: nextDate ? Timestamp.fromDate(nextDate) : null,
      serviceInterval: interval,
      serviceStatus: nextDate ? "UPCOMING" : null,
    };

    setLoading(true);
    try {
      let docRef = null;
      if (editId) {
        await updateDoc(doc(db, COLLECTION_NAME, editId), payload);
        docRef = doc(db, COLLECTION_NAME, editId);
      } else {
        const ref = await addDoc(collection(db, COLLECTION_NAME), payload);
        docRef = doc(db, COLLECTION_NAME, ref.id);
      }

      // handle incentive collection: create/update/delete incentive doc linked to this cashbook entry
      try {
        const incentiveVal = Number(form.incentive || 0);
        // find existing incentive doc for this cashbook entry
        const cashbookId = editId || (docRef && docRef.id);
        const q = query(collection(db, 'incentive'), where('cashbookId', '==', cashbookId));
        const incSnap = await getDocs(q);
        const existingInc = incSnap.docs.length ? { id: incSnap.docs[0].id, ...incSnap.docs[0].data() } : null;

        if (incentiveVal && incentiveVal > 0) {
          const incPayload = {
            amount: incentiveVal,
            remarks: form.incentiveremark || '',
            date: Timestamp.fromDate(new Date(form.date)),
            cashbookId: cashbookId,
            techno: form.techno || '',
            customer: form.customerdetail || '',
            createdAt: new Date(),
          };
          if (existingInc) {
            await updateDoc(doc(db, 'incentive', existingInc.id), incPayload);
            // ensure cashbook doc stores incentiveId
            await updateDoc(doc(db, COLLECTION_NAME, cashbookId), { incentiveId: existingInc.id });
          } else {
            const incRef = await addDoc(collection(db, 'incentive'), incPayload);
            await updateDoc(doc(db, COLLECTION_NAME, cashbookId), { incentiveId: incRef.id });
          }
        } else {
          // no incentive provided in form; if existing incentive doc exists, delete it
          if (existingInc) {
            await deleteDoc(doc(db, 'incentive', existingInc.id));
            await updateDoc(doc(db, COLLECTION_NAME, cashbookId), { incentiveId: null });
          }
        }
      } catch (ie) {
        console.error('Failed to sync incentive doc:', ie);
      }

      setShowForm(false);
      setEditId(null);

      const snap = await getDocs(collection(db, COLLECTION_NAME));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setData(rows);
    } catch (err) {
      console.error("Failed to save entry:", err);
      alert("Failed to save entry. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
      setData(data.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Failed to delete entry:", err);
      alert("Failed to delete entry. Check console for details.");
    } finally {
      setLoading(false);
    }
  };




  /* ---------------- PAGINATION ---------------- */
  const startIndex = (page - 1) * rowsPerPage;
  const pagedRows = filtered.slice(startIndex, startIndex + rowsPerPage);

  if (loading) {
    return <Loader />;
  }

  /* ---------------- UI ---------------- */
  return (
    <div className="container-fluid mt-4 px-4">

      {/* TABS */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'records' ? 'active' : ''}`}
            onClick={() => setActiveTab('records')}
          >
            Work / Job Entries ({data.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'expenses' ? 'active' : ''}`}
            onClick={() => setActiveTab('expenses')}
          >
            Expense Tracker ({expenseCount})
          </button>
        </li>
      </ul>

      {activeTab === 'records' && (
        <>
          {loading && <Loader />}
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Cashbook Records</h3>

            <div className="d-flex gap-2">
              <button className="btn btn-success" onClick={exportToExcel}>
                ⬇ Export Excel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setEditId(null);
                  setForm({
                    ticket: "",
                    worktype: "",
                    customerdetail: "",
                    mobile: "",
                    date: "",
                    income: "",
                    incomeremarks: "",
                    expense: "",
                    expenseremark: "",
                    profit: "",
                    techno: "",
                    createddate: "",
                    completeddate: "",
                    remarks: "",
                    incentive: "",
                    incentiveremark: "",
                    paymentStatus: "Pending",
                    pendingAmount: "",
                    paymentRemarks: "",

                  });
                  setShowForm(true);
                }}
              >
                ➕ Add Entry
              </button>
            </div>
          </div>

          {/* FILTERS */}
          <div className="row mb-3">
            <div className="col">
              <input
                className="form-control"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col">
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col">
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col">
              <select
                className="form-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="ALL">All Work Types</option>
                {categories.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All Payment Status</option>
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
                <option value="Partial">Partial</option>
              </select>
            </div>
          </div>

          {/* SUMMARY TILES */}
          <div className="row text-center mb-4">
            <div className="col-md-4 mb-2">
              <div
                style={{
                  border: "2px solid #dc3545",
                  background: "#ffecec",
                  borderRadius: "12px",
                  padding: "16px",
                }}
              >
                <h6 className="text-danger fw-bold">Total Expense</h6>
                <h3>₹ {totalExpense.toLocaleString()}</h3>
              </div>
            </div>

            <div className="col-md-4 mb-2">
              <div
                style={{
                  border: "2px solid #28a745",
                  background: "#eaffea",
                  borderRadius: "12px",
                  padding: "16px",
                }}
              >
                <h6 className="text-success fw-bold">Total Income</h6>
                <h3>₹ {totalIncome.toLocaleString()}</h3>
              </div>
            </div>

            <div className="col-md-4 mb-2">
              <div
                style={{
                  border: "2px solid #0d6efd",
                  background: "#e6f0ff",
                  borderRadius: "12px",
                  padding: "16px",
                }}
              >
                <h6 className="text-primary fw-bold">Total Profit</h6>
                <h3>₹ {totalProfit.toLocaleString()}</h3>
              </div>
            </div>
          </div>



          {/* TABLE (HORIZONTAL SCROLL FIXED) */}
          <div
            className="table-responsive"
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
          >
            <table
              className="table table-bordered table-hover"
              style={{ minWidth: "1600px" }}
            >
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>CUSTOMER DETAIL</th>
                  <th>MOBILE</th>
                  <th>PROFIT</th>
                  <th>INCOME REMARKS</th>
                  <th>TECHNO</th>
                  <th>DATE</th>
                  <th>INCOME</th>
                  <th>TICKET</th>
                  <th>WORK TYPE</th>
                  <th>EXPENSE</th>
                  <th>EXPENSE REMARK</th>
                  <th>INCENTIVE</th>
                  <th>INCENTIVE REMARK</th>
                  <th>PAYMENT STATUS</th>
                  <th>PENDING AMOUNT</th>
                  <th>PAYMENT REMARKS</th>
                  <th>REMARKS   </th>
                  <th style={{ position: "sticky", right: 0, background: "#212529", zIndex: 3, }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r, i) => (
                  <tr key={r.id}>
                    <td>{startIndex + i + 1}</td>
                    <td>{r.customerdetail}</td>
                    <td>{r.mobile}</td>
                    <td>{r.profit}</td>
                    <td>{r.incomeremarks}</td>
                    <td>{r.techno}</td>
                    <td>{formatDate(r.date)}</td>
                    <td>{r.income}</td>
                    <td>{r.ticket}</td>
                    <td>{r.worktype}</td>
                    <td>{r.expense}</td>
                    <td>{r.expenseremark}</td>
                    <td>{r.incentive}</td>
                    <td>{r.incentiveremark}</td>
                    <td>
                      <span
                        className={`badge ${r.paymentStatus === "Paid"
                          ? "bg-success"
                          : r.paymentStatus === "Pending"
                            ? "bg-danger"
                            : "bg-warning text-dark"
                          }`}
                      >
                        {r.paymentStatus || "Pending"}
                      </span>
                    </td>
                    <td>{r.pendingAmount}</td>
                    <td>{r.paymentRemarks}</td>
                    <td>{r.remarks}</td>
                    <td
                      style={{
                        position: "sticky",
                        right: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      <button
                        className="btn btn-sm btn-warning me-2"
                        onClick={() => {
                          setEditId(r.id);
                          setForm({
                            ...r,
                            date: formatDate(r.date).split("/").reverse().join("-"),
                          });
                          setShowForm(true);
                        }}
                      >
                        Edit
                      </button>

                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteEntry(r.id)}
                      >
                        Delete
                      </button>
                    </td>



                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {filtered.length > 0 && (
            <div className={"d-flex justify-content-between align-items-center mt-3"}>
              <div className="d-flex align-items-center gap-2">
                <span>Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>entries</span>
                <span className="ms-2 text-muted small">
                  Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, filtered.length)} of {filtered.length}
                </span>
              </div>

              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(page - 1)}>Previous</button>
                  </li>
                  {(() => {
                    const totalPages = Math.ceil(filtered.length / rowsPerPage);
                    return [...Array(totalPages)].map((_, i) => {
                      const p = i + 1;
                      if (
                        p === 1 ||
                        p === totalPages ||
                        (p >= page - 2 && p <= page + 2)
                      ) {
                        return (
                          <li key={p} className={`page-item ${page === p ? "active" : ""}`}>
                            <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                          </li>
                        );
                      } else if (
                        (p === page - 3 && page > 4) ||
                        (p === page + 3 && page < totalPages - 3)
                      ) {
                        return <li key={p} className="page-item disabled"><span className="page-link">...</span></li>;
                      }
                      return null;
                    });
                  })()}
                  <li className={`page-item ${page === Math.ceil(filtered.length / rowsPerPage) ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(page + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}

          {showForm && (
            <div className="modal show d-block" style={{ background: "#00000080" }}>
              <div className="modal-dialog modal-xl">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editId ? "Edit Entry" : "Add Entry"}
                    </h5>
                    <button className="btn-close" onClick={() => setShowForm(false)} />
                  </div>

                  <div className="modal-body">
                    <div className="row g-2">

                      {/* Ticket Number */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Ticket Number"
                            value={form.ticket}
                            onChange={(e) =>
                              setForm({ ...form, ticket: e.target.value })
                            }
                          />
                          <label>Ticket Number</label>
                        </div>
                      </div>

                      {/* Work Type */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <select
                            className="form-select"
                            value={form.worktype}
                            onChange={(e) =>
                              setForm({ ...form, worktype: e.target.value })
                            }
                          >
                            <option value="">Select Work Type</option>
                            <option value="Installation">Installation</option>
                            <option value="Breakdown">Breakdown</option>
                            <option value="Regular Service">Service</option>
                            <option value="AMC">AMC</option>
                          </select>
                          <label>Work Type</label>
                        </div>
                      </div>

                      {/* Customer Detail */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Customer Detail"
                            value={form.customerdetail}
                            onChange={(e) =>
                              setForm({ ...form, customerdetail: e.target.value })
                            }
                          />
                          <label>Customer Detail</label>
                        </div>
                      </div>

                      {/* Mobile */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Customer Mobile Number"
                            value={form.mobile}
                            onChange={(e) =>
                              setForm({ ...form, mobile: e.target.value })
                            }
                          />
                          <label>Customer Mobile Number</label>
                        </div>
                      </div>

                      {/* Entry Date – FULL WIDTH */}
                      <div className="col-12">
                        <div className="form-floating">
                          <input
                            type="date"
                            className="form-control"
                            value={form.date}
                            onChange={(e) =>
                              setForm({ ...form, date: e.target.value })
                            }
                          />
                          <label>Entry Date</label>
                        </div>
                      </div>

                      {/* Income */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Income"
                            value={form.income}
                            onChange={(e) =>
                              setForm({ ...form, income: e.target.value })
                            }
                          />
                          <label>Income</label>
                        </div>
                      </div>

                      {/* Income Remarks */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Income Remarks"
                            value={form.incomeremarks}
                            onChange={(e) =>
                              setForm({ ...form, incomeremarks: e.target.value })
                            }
                          />
                          <label>Income Remarks</label>
                        </div>
                      </div>

                      {/* Expense */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Expense"
                            value={form.expense}
                            onChange={(e) =>
                              setForm({ ...form, expense: e.target.value })
                            }
                          />
                          <label>Expense</label>
                        </div>
                      </div>

                      {/* Expense Remarks */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Expense Remarks"
                            value={form.expenseremark}
                            onChange={(e) =>
                              setForm({ ...form, expenseremark: e.target.value })
                            }
                          />
                          <label>Expense Remarks</label>
                        </div>
                      </div>
                      {/* incentives */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Incentive"
                            value={form.incentive}
                            onChange={(e) =>
                              setForm({ ...form, incentive: e.target.value })
                            }
                          />
                          <label>Incentive</label>
                        </div>
                      </div>
                      {/* Incentive Remarks */}
                      {/* incentives */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Incentive Remarks"
                            value={form.incentiveremark}
                            onChange={(e) =>
                              setForm({ ...form, incentiveremark: e.target.value })
                            }
                          />
                          <label>Incentive Remarks</label>
                        </div>
                      </div>
                      {/* Tech Number */}
                      <div className="col-md-6 md-3">

                        <select
                          className="form-control"
                          value={form.techno}
                          onChange={(e) =>
                            setForm({ ...form, techno: e.target.value })
                          }
                        >
                          <option value="">Select Technician Number ↓</option>

                          {technicians.map((t) => (
                            <option key={t.mobile} value={t.mobile}>
                              {t.mobile} ({t.name})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Created Date */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="date"
                            className="form-control"
                            value={form.createddate}
                            onChange={(e) =>
                              setForm({ ...form, createddate: e.target.value })
                            }
                          />
                          <label>Created Date</label>
                        </div>
                      </div>

                      {/* Payment Status */}
                      <div className="col-md-4">
                        <div className="form-floating">
                          <select
                            className="form-select"
                            value={form.paymentStatus}
                            onChange={(e) =>
                              setForm({ ...form, paymentStatus: e.target.value })
                            }
                          >
                            <option value="Pending">Pending</option>
                            <option value="Partial">Partial</option>
                            <option value="Paid">Paid</option>
                          </select>
                          <label>Payment Status</label>
                        </div>
                      </div>

                      {/* Pending Amount - Visible only if Pending or Partial */}
                      {(form.paymentStatus === "Pending" ||
                        form.paymentStatus === "Partial") && (
                          <div className="col-md-4">
                            <div className="form-floating">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Pending Amount"
                                value={form.pendingAmount}
                                onChange={(e) =>
                                  setForm({ ...form, pendingAmount: e.target.value })
                                }
                              />
                              <label>Pending Amount</label>
                            </div>
                          </div>
                        )}

                      {/* Payment Remarks */}
                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Payment Remarks"
                            value={form.paymentRemarks}
                            onChange={(e) =>
                              setForm({ ...form, paymentRemarks: e.target.value })
                            }
                          />
                          <label>Payment Remarks</label>
                        </div>
                      </div>

                      {/* Completed Date */}
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="date"
                            className="form-control"
                            value={form.completeddate}
                            onChange={(e) =>
                              setForm({ ...form, completeddate: e.target.value })
                            }
                          />
                          <label>Completed Date</label>
                        </div>
                      </div>

                      {/* Remarks – FULL WIDTH */}
                      <div className="col-12">
                        <div className="form-floating">
                          <textarea
                            className="form-control"
                            placeholder="Remarks"
                            style={{ height: "90px" }}
                            value={form.remarks}
                            onChange={(e) =>
                              setForm({ ...form, remarks: e.target.value })
                            }
                          />
                          <label>Remarks</label>
                        </div>
                      </div>
                      <br />

                    </div>
                  </div>


                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-success" onClick={saveEntry}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </>
      )}

      {activeTab === 'expenses' && <ExpenseTracker />}


    </div>
  );
}

export default Cashbook;
